import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import {
  getRequirementDataForPosition,
  type PositionRequirementRow,
} from 'api/company/position/requirement/getRequirementDataForPosition'
import { requirementDataForPositionKey } from './keys'

type RequirementPositionBody = Awaited<
  ReturnType<typeof getRequirementDataForPosition>
>

export type RequirementDataForPositionResult = {
  positionData: RequirementPositionBody['data']['admin']['positions']
  positionRequirementData: (PositionRequirementRow & { mustHave: boolean })[]
}

function parseRequirementPositionData(
  body: RequirementPositionBody
): RequirementDataForPositionResult {
  const positions = body.data.admin.positions
  if (!positions?.length) {
    return {
      positionData: [],
      positionRequirementData: [],
    } as RequirementDataForPositionResult
  }

  const mustHaveRequirementIds = positions[0].mustHaveRequirements.map(
    (r) => r.id
  )
  const rows = positions[0].requirements2?.map((r) => {
    return { ...r, mustHave: mustHaveRequirementIds?.includes(r.id) ?? false }
  })

  return {
    positionData: positions,
    positionRequirementData: rows ?? [],
  }
}

/**
 * Position row plus merged must-have flags for the requirements table.
 */
export const useRequirementDataForPositionQuery = (
  positionId: number | null,
  options?: Partial<UseQueryOptions<RequirementDataForPositionResult>>
) => {
  return useQuery<
    RequirementDataForPositionResult,
    Error,
    RequirementDataForPositionResult
  >({
    ...options,
    queryKey: requirementDataForPositionKey(positionId),
    queryFn: async () => {
      if (!positionId) {
        return { positionData: [], positionRequirementData: [] }
      }
      const body = await getRequirementDataForPosition(positionId)
      return parseRequirementPositionData(body)
    },
  })
}
